import { useAuth } from "@/context/AuthContext";
import { Pencil, Trash2, Stethoscope, FileText, Phone } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = {
  Ongoing: "bg-blue-50 text-blue-700", Discharged: "bg-emerald-50 text-emerald-700",
  "Referred out": "bg-amber-50 text-amber-700", Inactive: "bg-gray-100 text-gray-500",
};

export default function MedicalProviderList({ providers, showClient, onEdit, onDelete }) {
  const { user } = useAuth();
  if (!providers.length) {
    return <p className="text-xs text-gray-400 text-center py-8 border border-dashed border-gray-200 rounded-sm" data-testid="mp-empty">No medical providers recorded.</p>;
  }
  return (
    <div className="space-y-2" data-testid="mp-list">
      {providers.map((p) => {
        const pending = p.records_requested && !p.records_received;
        return (
          <div key={p.id} data-testid={`mp-row-${p.id}`}
            className="bg-white border border-gray-200 rounded-sm p-4 flex items-start justify-between gap-4 hover:border-gray-300 transition-colors">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Stethoscope className="w-4 h-4 text-[#002FA7] shrink-0" />
                <span className="font-semibold text-gray-900">{p.provider_name}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.provider_type}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS[p.treatment_status] || ""}`}>{p.treatment_status}</span>
                {p.has_lien && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Lien {fmt(p.lien_amount)}</span>}
              </div>
              {showClient && p.client_name && <p className="text-xs text-gray-500 mt-1">{p.client_name}</p>}
              {p.diagnosis && <p className="text-sm text-gray-700 mt-1 truncate">{p.diagnosis}</p>}
              <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-gray-400">
                {p.first_visit_date && <span>First: {p.first_visit_date.slice(0, 10)}</span>}
                {p.last_visit_date && <span>Last: {p.last_visit_date.slice(0, 10)}</span>}
                {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                {p.billed_amount > 0 && <span className="font-semibold text-gray-600">Billed {fmt(p.billed_amount)}</span>}
                <span className={`inline-flex items-center gap-1 font-semibold ${pending ? "text-amber-600" : p.records_received ? "text-emerald-600" : "text-gray-400"}`}>
                  <FileText className="w-3 h-3" />
                  {p.records_received ? "Records received" : pending ? "Records pending" : "No records request"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => onEdit(p)} data-testid={`mp-edit-${p.id}`} className="text-gray-400 hover:text-[#002FA7] p-1.5"><Pencil className="w-4 h-4" /></button>
              {user?.role === "admin" && (
                <button onClick={() => onDelete(p)} data-testid={`mp-delete-${p.id}`} className="text-gray-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
